import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Info as InfoIcon,
  RequestQuote as RequestQuoteIcon
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../redux/store';
import {
  fetchPayoutRecords,
  fetchPayoutSettings,
  requestPayout,
  PayoutRecord
} from '../../redux/revenueSlice';
import ToastService from '../../core/notifications/ToastService';

export const PayoutHistoryPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { payoutRecords, payoutSettings, isLoading, error } = useSelector(
    (state: RootState) => state.revenue
  );
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);

  useEffect(() => {
    dispatch(fetchPayoutRecords({}));
    dispatch(fetchPayoutSettings());
  }, [dispatch]);

  const handleRequestPayout = async () => {
    setRequestLoading(true);
    try {
      await dispatch(requestPayout()).unwrap();
      ToastService.success('Payout request submitted successfully');
      setShowRequestDialog(false);
      dispatch(fetchPayoutRecords({}));
    } catch (err: any) {
      ToastService.error(err || 'Failed to request payout');
    } finally {
      setRequestLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      pending: '#F59E0B',
      processing: '#3B82F6',
      completed: '#10B981',
      failed: '#EF4444',
      cancelled: '#6B7280'
    };
    return colors[status] || '#6B7280';
  };

  const getStatusIcon = (status: string) => {
    const icons: Record<string, string> = {
      pending: '⏳',
      processing: '⚙️',
      completed: '✅',
      failed: '❌',
      cancelled: '🚫'
    };
    return icons[status] || '•';
  };

  if (error) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
            Payout History
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Track all your payouts and payment status
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<RequestQuoteIcon />}
          onClick={() => setShowRequestDialog(true)}
          sx={{ backgroundColor: '#14B8A6' }}
        >
          Request Payout
        </Button>
      </Box>

      {/* Payout Settings Info */}
      {payoutSettings && (
        <Card sx={{ mb: 4, backgroundColor: '#f0fffe', borderLeft: '4px solid #14B8A6' }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
              <Box>
                <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                  <strong>Payout Schedule:</strong> Every {payoutSettings.payoutDay} ({payoutSettings.payoutFrequency})
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                  <strong>Minimum Amount:</strong> ₹{payoutSettings.minimumPayoutAmount.toFixed(2)}
                </Typography>
                <Typography variant="body2" sx={{ color: '#666' }}>
                  <strong>Processing Time:</strong> {payoutSettings.payoutProcessingDays} business days
                </Typography>
              </Box>
              <Button
                size="small"
                startIcon={<InfoIcon />}
                variant="text"
              >
                Learn More
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Payout Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
            Payout Records
          </Typography>

          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : payoutRecords.length === 0 ? (
            <Typography variant="body2" sx={{ color: '#999', textAlign: 'center', py: 4 }}>
              No payout records found
            </Typography>
          ) : (
            <TableContainer component={Paper} sx={{ backgroundColor: '#fff' }}>
              <Table>
                <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Payout ID</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Scheduled Date</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Completed Date</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Transaction ID</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payoutRecords.map((payout: PayoutRecord) => (
                    <TableRow key={payout.id} sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>#{payout.id}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', color: '#10B981' }}>
                        ₹{payout.payoutAmount.toFixed(2)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={<span>{getStatusIcon(payout.status)}</span>}
                          label={payout.status}
                          size="small"
                          sx={{
                            backgroundColor: getStatusColor(payout.status) + '20',
                            color: getStatusColor(payout.status),
                            fontWeight: 'bold'
                          }}
                        />
                      </TableCell>
                      <TableCell>{payout.payoutDate}</TableCell>
                      <TableCell>{payout.completedDate || '-'}</TableCell>
                      <TableCell>{payout.transactionId || '-'}</TableCell>
                      <TableCell>
                        <Button size="small" variant="text">
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Request Payout Dialog */}
      <Dialog open={showRequestDialog} onClose={() => setShowRequestDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Request Payout</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
            Request an immediate payout of your available balance. The payout will be processed to your primary bank account.
          </Typography>

          {payoutSettings && (
            <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1, mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Minimum Payout Amount:</strong> ₹{payoutSettings.minimumPayoutAmount.toFixed(2)}
              </Typography>
              <Typography variant="body2">
                <strong>Processing Time:</strong> {payoutSettings.payoutProcessingDays} business days
              </Typography>
            </Box>
          )}

          <Alert severity="info" sx={{ mb: 2 }}>
            Make sure you have a verified bank account before requesting a payout.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRequestDialog(false)}>Cancel</Button>
          <Button
            onClick={handleRequestPayout}
            variant="contained"
            disabled={requestLoading}
            sx={{ backgroundColor: '#14B8A6' }}
          >
            {requestLoading ? <CircularProgress size={24} /> : 'Request Payout'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PayoutHistoryPage;
